import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSocialStreamPlatforms,
  isCustomersLoaded,
} from 'redux/reducers/customers';
import {
  defaultPlatforms,
  platformDefaultColor,
  DOUYIN_ID,
  TIK_TOK_ID,
} from 'components/TopicFilters/constants/platformFilter.contants';
import generateRandomColor from 'helpers/generateRandomColor';
import generateShadedColor from 'helpers/generateShadedColor';

const usePlatforms = (filters: any): any => {
  const customersList = useSelector(selectSocialStreamPlatforms);
  const isPlatformLoaded = useSelector(isCustomersLoaded);
  const isChina = filters?.country === 'CN';

  const platforms = useMemo(() => {
    const customersData = customersList?.length
      ? customersList
      : defaultPlatforms;

    return customersData.reduce((acc: any[], item: any) => {
      if (
        (isChina && item.id === TIK_TOK_ID) ||
        (!isChina && item.id === DOUYIN_ID)
      )
        return acc;
      // get color of default backend color or create a random one
      const platformColor = item?.color || generateRandomColor();
      const creationsPlatformColor = generateRandomColor();

      return [
        ...acc,
        {
          ...item,
          platformColor,
          // get default hardcoded in all project colors (I saved it for the same view) or get backend/random data
          color: platformDefaultColor[item.name] || {
            views: {
              ugc: generateShadedColor(platformColor, -40),
              pgc: generateShadedColor(platformColor, -5),
            },
            creations: {
              ugc: generateShadedColor(creationsPlatformColor, -40),
              pgc: generateShadedColor(creationsPlatformColor, -5),
            },
          },
        },
      ];
    }, []);
  }, [customersList, isChina]);

  const platformColors = useMemo(
    () =>
      platforms.reduce(
        (acc: any, item: any) => ({
          ...acc,
          [item.name]: item.color,
        }),
        {}
      ),
    [platforms]
  );

  const platformsList = useMemo(
    (): any =>
      platforms
        ?.map((item: any) => ({
          id: item.id,
          name: item.name,
          label: item.displayName,
          icon: item.thumbnailUrl,
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name)),
    [platforms]
  );

  const platformHash = useMemo(
    () =>
      platformsList.reduce(
        (acc: any, item: any) => ({
          ...acc,
          [item.id]: item,
        }),
        {}
      ),
    [platforms]
  );

  return {
    isPlatformLoaded,
    platformColors,
    platformsList,
    platforms,
    platformHash,
    selectedPlatform: filters?.platform ? platformHash[filters.platform] : null,
  };
};

export default usePlatforms;
